import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Users, FileText, Building2, CheckCircle, Flame, ClipboardList, BarChart3, Folder, AlertCircle } from 'lucide-react';

interface CaseManagerDashboardProps {
  dashboardData: any;
  onDataUpdate: () => void;
}

const Container = styled.div`
  margin-top: 24px;
`;

// Summary Cards Grid
const SummaryCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Status Distribution Grid - flexible grid that expands to fill width
const StatusDistributionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
`;

const SummaryCard = styled.div<{ borderColor: string }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  padding-top: 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.borderColor};
    filter: saturate(0.7) brightness(0.9);
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const CardIcon = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => `${props.$color}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};
  filter: saturate(0.7) brightness(0.9);
`;

const CardTitle = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const CardValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 4px;
`;

const CardSubtext = styled.div`
  font-size: 13px;
  color: var(--text-tertiary);
`;

// Urgent Tasks Section
const Section = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 28px;
  margin-bottom: 28px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SectionIcon = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  color: ${props => props.$color || 'var(--primary-color)'};
  filter: saturate(0.7) brightness(0.9);
`;

const UrgentTasksTable = styled.div`
  overflow-x: auto;
`;

const TaskRow = styled.div`
  display: grid;
  grid-template-columns: 40px 2fr 1.5fr 120px 100px 120px;
  gap: 16px;
  padding: 12px 8px;
  border-bottom: 1px solid var(--border-color);
  align-items: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-color);
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: 40px 1fr;
    gap: 8px;
  }
`;

const TaskIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--primary-light);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const TaskTitle = styled.div`
  font-weight: 500;
  color: var(--text-primary);

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ClientName = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const DueDate = styled.div<{ isOverdue?: boolean }>`
  color: ${props => props.isOverdue ? 'var(--error-color)' : 'var(--text-secondary)'};
  font-size: 14px;
  font-weight: ${props => props.isOverdue ? '600' : '400'};

  @media (max-width: 768px) {
    display: none;
  }
`;

const PriorityBadge = styled.div<{ priority: string }>`
  padding: 4px 12px;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  background: ${props => {
    switch (props.priority) {
      case 'High': return 'var(--error-light)';
      case 'Medium': return 'var(--warning-light)';
      default: return 'var(--info-light)';
    }
  }};
  color: ${props => {
    switch (props.priority) {
      case 'High': return 'var(--error-color)';
      case 'Medium': return 'var(--warning-color)';
      default: return 'var(--info-color)';
    }
  }};

  @media (max-width: 768px) {
    display: none;
  }
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  background: var(--primary-color);
  color: white;
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: transparent;
    color: var(--primary-color);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// Clients Table
const ClientsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: right;
  padding: 12px 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  border-bottom: 2px solid var(--border-color);
  background: var(--background-color);
`;

const Td = styled.td`
  padding: 12px 8px;
  font-size: 14px;
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-color);
`;

const StatusBadge = styled.span<{ color?: string }>`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.color || 'var(--text-secondary)'};
  filter: saturate(0.7) brightness(0.85);
`;

const ClientLink = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const formatDate = (date: string | Date): string => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('he-IL');
};

const CaseManagerDashboard: React.FC<CaseManagerDashboardProps> = ({ dashboardData, onDataUpdate }) => {
  const navigate = useNavigate();

  const caseManagerData = dashboardData.caseManagerData;

  if (!caseManagerData) {
    return null;
  }

  const handleClientClick = (clientId: number) => {
    navigate(`/clients/${clientId}/manage`);
  };

  const handleCommitteeClick = (clientId: number) => {
    navigate(`/clients/${clientId}/manage/process-committees`);
  };

  const getPriorityText = (priority: string): string => {
    switch (priority) {
      case 'High': return 'גבוה';
      case 'Medium': return 'בינוני';
      case 'Low': return 'נמוך';
      default: return priority;
    }
  };

  return (
    <Container>
      {/* Summary Cards */}
      <SummaryCardsGrid>
        <SummaryCard borderColor="var(--primary-color)">
          <CardHeader>
            <CardIcon $color="var(--primary-color)">
              <Users size={20} />
            </CardIcon>
            <CardTitle>לקוחות פעילים</CardTitle>
          </CardHeader>
          <CardValue>{caseManagerData.activeCasesCount}</CardValue>
          <CardSubtext>בטיפול שוטף</CardSubtext>
        </SummaryCard>

        <SummaryCard borderColor="var(--warning-color)">
          <CardHeader>
            <CardIcon $color="var(--warning-color)">
              <FileText size={20} />
            </CardIcon>
            <CardTitle>מסמכים ממתינים</CardTitle>
          </CardHeader>
          <CardValue>{caseManagerData.pendingDocumentsCount}</CardValue>
          <CardSubtext>לאישור</CardSubtext>
        </SummaryCard>

        <SummaryCard borderColor="var(--info-color)">
          <CardHeader>
            <CardIcon $color="var(--info-color)">
              <Building2 size={20} />
            </CardIcon>
            <CardTitle>ועדות קרובות</CardTitle>
          </CardHeader>
          <CardValue>{caseManagerData.upcomingCommitteesCount}</CardValue>
          <CardSubtext>ב-7 הימים הקרובים</CardSubtext>
        </SummaryCard>

        <SummaryCard borderColor="var(--success-color)">
          <CardHeader>
            <CardIcon $color="var(--success-color)">
              <CheckCircle size={20} />
            </CardIcon>
            <CardTitle>אושרו החודש</CardTitle>
          </CardHeader>
          <CardValue>{caseManagerData.completedThisMonthCount}</CardValue>
          <CardSubtext>תיקים שאושרו</CardSubtext>
        </SummaryCard>
      </SummaryCardsGrid>

      {/* Urgent Tasks */}
      {caseManagerData.urgentTasks && caseManagerData.urgentTasks.length > 0 && (
        <Section>
          <SectionTitle>
            <SectionIcon $color="#dc2626">
              <Flame size={20} />
            </SectionIcon>
            דורש טיפול מיידי
            {caseManagerData.overdueRemindersCount > 0 && (
              <span style={{ color: 'var(--error-color)', fontSize: '14px', marginRight: '8px' }}>
                ({caseManagerData.overdueRemindersCount} תזכורות באיחור)
              </span>
            )}
          </SectionTitle>
          <UrgentTasksTable>
            {caseManagerData.urgentTasks.map((task: any) => {
              const handleTaskClick = () => {
                if (task.taskType === 'Committee') {
                  handleCommitteeClick(task.clientId);
                } else {
                  handleClientClick(task.clientId);
                }
              };

              return (
                <TaskRow key={`${task.taskType}-${task.id}`}>
                  <TaskIcon>
                    <ClipboardList size={16} />
                  </TaskIcon>
                  <TaskTitle>{task.title}</TaskTitle>
                  <ClientName onClick={() => handleClientClick(task.clientId)}>
                    {task.clientName}
                  </ClientName>
                  <DueDate isOverdue={task.daysOverdue > 0}>
                    {task.daysOverdue > 0
                      ? `באיחור ${task.daysOverdue} ימים`
                      : formatDate(task.dueDate)
                    }
                  </DueDate>
                  <PriorityBadge priority={task.priority}>
                    {getPriorityText(task.priority)}
                  </PriorityBadge>
                  <ActionButton onClick={handleTaskClick}>
                    טפל עכשיו
                  </ActionButton>
                </TaskRow>
              );
            })}
          </UrgentTasksTable>
        </Section>
      )}

      {/* My Clients */}
      {caseManagerData.myClients && caseManagerData.myClients.length > 0 && (
        <Section>
          <SectionTitle>
            <SectionIcon $color="var(--primary-color)">
              <ClipboardList size={20} />
            </SectionIcon>
            הלקוחות שלי
          </SectionTitle>
          <ClientsTable>
            <thead>
              <tr>
                <Th>שם לקוח</Th>
                <Th>סטטוס</Th>
                <Th>ועדה הבאה</Th>
                <Th>ימים בטיפול</Th>
                <Th>עודכן לאחרונה</Th>
                <Th>פעולות</Th>
              </tr>
            </thead>
            <tbody>
              {caseManagerData.myClients.map((client: any) => (
                <tr key={client.id}>
                  <Td>
                    <ClientLink onClick={() => handleClientClick(client.id)}>
                      {client.fullName}
                    </ClientLink>
                    {client.hasUrgentTask && (
                      <AlertCircle size={14} style={{ color: 'var(--error-color)', marginRight: '8px' }} />
                    )}
                  </Td>
                  <Td>
                    {client.currentStatusName && (
                      <StatusBadge color={client.currentStatusColor}>
                        {client.currentStatusName}
                      </StatusBadge>
                    )}
                  </Td>
                  <Td>
                    {client.nextCommitteeDate ? formatDate(client.nextCommitteeDate) : '-'}
                  </Td>
                  <Td>{client.daysInTreatment} ימים</Td>
                  <Td>
                    <span style={{
                      color: client.daysSinceLastUpdate > 14 ? 'var(--error-color)' : 'var(--text-secondary)'
                    }}>
                      לפני {client.daysSinceLastUpdate} ימים
                    </span>
                  </Td>
                  <Td>
                    <ActionButton onClick={() => handleClientClick(client.id)}>
                      פתח תיק
                    </ActionButton>
                  </Td>
                </tr>
              ))}
            </tbody>
          </ClientsTable>
        </Section>
      )}

      {/* Status Distribution */}
      {caseManagerData.clientStatusDistribution && caseManagerData.clientStatusDistribution.length > 0 && (
        <Section>
          <SectionTitle>
            <SectionIcon $color="#6366f1">
              <BarChart3 size={20} />
            </SectionIcon>
            התפלגות לפי סטטוס
          </SectionTitle>
          <StatusDistributionGrid>
            {caseManagerData.clientStatusDistribution.map((status: any) => (
              <SummaryCard key={status.statusId} borderColor={status.statusColor || 'var(--primary-color)'}>
                <CardHeader>
                  <CardIcon $color={status.statusColor || 'var(--primary-color)'}>
                    <BarChart3 size={20} />
                  </CardIcon>
                  <CardTitle>{status.statusName}</CardTitle>
                </CardHeader>
                <CardValue>{status.count}</CardValue>
                <CardSubtext>{status.percentage}% מהלקוחות</CardSubtext>
              </SummaryCard>
            ))}
          </StatusDistributionGrid>
        </Section>
      )}

      {/* Team Overview - for supervisors */}
      {caseManagerData.teamOverview && (
        <Section>
          <SectionTitle>
            <SectionIcon $color="var(--primary-color)">
              <Users size={20} />
            </SectionIcon>
            סקירת צוות
          </SectionTitle>
          <SummaryCardsGrid>
            <SummaryCard borderColor="var(--primary-color)">
              <CardHeader>
                <CardIcon $color="var(--primary-color)">
                  <Folder size={20} />
                </CardIcon>
                <CardTitle>סה"כ לקוחות בצוות</CardTitle>
              </CardHeader>
              <CardValue>{caseManagerData.teamOverview.totalTeamClients}</CardValue>
            </SummaryCard>

            <SummaryCard borderColor="var(--warning-color)">
              <CardHeader>
                <CardIcon $color="var(--warning-color)">
                  <FileText size={20} />
                </CardIcon>
                <CardTitle>מסמכים ממתינים</CardTitle>
              </CardHeader>
              <CardValue>{caseManagerData.teamOverview.totalTeamPendingDocuments}</CardValue>
            </SummaryCard>

            <SummaryCard borderColor="var(--info-color)">
              <CardHeader>
                <CardIcon $color="var(--info-color)">
                  <Building2 size={20} />
                </CardIcon>
                <CardTitle>ועדות קרובות</CardTitle>
              </CardHeader>
              <CardValue>{caseManagerData.teamOverview.totalTeamUpcomingCommittees}</CardValue>
            </SummaryCard>

            <SummaryCard borderColor="var(--success-color)">
              <CardHeader>
                <CardIcon $color="var(--success-color)">
                  <CheckCircle size={20} />
                </CardIcon>
                <CardTitle>אושרו החודש</CardTitle>
              </CardHeader>
              <CardValue>{caseManagerData.teamOverview.totalTeamCompletedThisMonth}</CardValue>
            </SummaryCard>
          </SummaryCardsGrid>

          {caseManagerData.teamOverview.teamMembersLoad && caseManagerData.teamOverview.teamMembersLoad.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <ClientsTable>
                <thead>
                  <tr>
                    <Th>מנהל תיקים</Th>
                    <Th>לקוחות פעילים</Th>
                    <Th>משימות ממתינות</Th>
                    <Th>אושרו החודש</Th>
                  </tr>
                </thead>
                <tbody>
                  {caseManagerData.teamOverview.teamMembersLoad.map((member: any) => (
                    <tr key={member.userId}>
                      <Td style={{ fontWeight: 600 }}>{member.name}</Td>
                      <Td>{member.clientsCount}</Td>
                      <Td>{member.pendingTasksCount}</Td>
                      <Td>{member.completedThisMonth}</Td>
                    </tr>
                  ))}
                </tbody>
              </ClientsTable>
            </div>
          )}
        </Section>
      )}
    </Container>
  );
};

export default CaseManagerDashboard;